const NUMERIC_MONTHS: Record<string, string> = {
  '01': 'Jan',
  '02': 'Feb',
  '03': 'Mar',
  '04': 'Apr',
  '05': 'May',
  '06': 'Jun',
  '07': 'Jul',
  '08': 'Aug',
  '09': 'Sep',
  '10': 'Oct',
  '11': 'Nov',
  '12': 'Dec',
};

const VALID_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const isValidMonthAbbrev = (str: string): boolean => {
  return VALID_MONTHS.some(month => month.toLowerCase() === str.toLowerCase());
};

const normalizeMonthName = (month: string): string => {
  return month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
};

const getMonthFromNumeric = (numericMonth: string): string | null => {
  return NUMERIC_MONTHS[numericMonth] || null;
};

const formatFirstDate = (result: string): string | null => {
  // Handle pasted date with separators: 01/01/2025 or 01-01-2025 or 01.01.2025 → 01 Jan 2025 -
  const separatedFullDateMatch = result.match(/^(\d{1,2})[/\-_.](\d{2})[/\-_.](\d{4})$/);
  if (separatedFullDateMatch) {
    const [, day, numMonth, year] = separatedFullDateMatch;
    const dayNum = parseInt(day, 10);
    const monthName = getMonthFromNumeric(numMonth);
    if (dayNum >= 1 && dayNum <= 31 && monthName) {
      return `${day.padStart(2, '0')} ${monthName} ${year} - `;
    }
  }

  // Handle pasted date with text month and separators: 01/Jan/2025, 01-jan-2025, 1/Jan/2025 → 01 Jan 2025 -
  const separatedTextMonthMatch = result.match(/^(\d{1,2})[/\-_.]([A-Za-z]{3})[/\-_.](\d{4})$/);
  if (separatedTextMonthMatch && isValidMonthAbbrev(separatedTextMonthMatch[2])) {
    const [, day, monthStr, year] = separatedTextMonthMatch;
    const dayNum = parseInt(day, 10);
    const month = normalizeMonthName(monthStr);
    if (dayNum >= 1 && dayNum <= 31) {
      return `${day.padStart(2, '0')} ${month} ${year} - `;
    }
  }

  // Handle date with space before separator: "01 /Jan/2025", "01 /jan/2021" → 01 Jan 2025 -
  const spaceBeforeSeparatorTextMonthMatch = result.match(/^(\d{1,2})\s*[/\-_.]([A-Za-z]{3})[/\-_.](\d{4})$/);
  if (spaceBeforeSeparatorTextMonthMatch && isValidMonthAbbrev(spaceBeforeSeparatorTextMonthMatch[2])) {
    const [, day, monthStr, year] = spaceBeforeSeparatorTextMonthMatch;
    const dayNum = parseInt(day, 10);
    const month = normalizeMonthName(monthStr);
    if (dayNum >= 1 && dayNum <= 31) {
      return `${day.padStart(2, '0')} ${month} ${year} - `;
    }
  }

  // Handle date with space before separator and numeric month: "01 /01/2025" → 01 Jan 2025 -
  const spaceBeforeSeparatorNumericMonthMatch = result.match(/^(\d{1,2})\s*[/\-_.](\d{2})[/\-_.](\d{4})$/);
  if (spaceBeforeSeparatorNumericMonthMatch) {
    const [, day, numMonth, year] = spaceBeforeSeparatorNumericMonthMatch;
    const dayNum = parseInt(day, 10);
    const monthName = getMonthFromNumeric(numMonth);
    if (dayNum >= 1 && dayNum <= 31 && monthName) {
      return `${day.padStart(2, '0')} ${monthName} ${year} - `;
    }
  }

  // Handle compact format without spaces: 01012025 → 01 Jan 2025 -
  const compactFullDateMatch = result.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (compactFullDateMatch) {
    const [, day, numMonth, year] = compactFullDateMatch;
    const dayNum = parseInt(day, 10);
    const monthName = getMonthFromNumeric(numMonth);
    if (dayNum >= 1 && dayNum <= 31 && monthName) {
      return `${day} ${monthName} ${year} - `;
    }
  }

  // Handle compact format: 0101 → 01 Jan (partial, 4 digits for day+month)
  const compactDayMonthMatch = result.match(/^(\d{2})(\d{2})$/);
  if (compactDayMonthMatch) {
    const [, day, numMonth] = compactDayMonthMatch;
    const dayNum = parseInt(day, 10);
    const monthName = getMonthFromNumeric(numMonth);
    if (dayNum >= 1 && dayNum <= 31 && monthName) {
      return `${day} ${monthName} `;
    }
  }

  // Handle day followed by numeric month: "01 01" or "01 12" → "01 Jan " or "01 Dec "
  const dayNumericMonthMatch = result.match(/^(\d{1,2})\s+(\d{2})$/);
  if (dayNumericMonthMatch) {
    const [, day, numMonth] = dayNumericMonthMatch;
    const dayNum = parseInt(day, 10);
    const monthName = getMonthFromNumeric(numMonth);
    if (dayNum >= 1 && dayNum <= 31 && monthName) {
      return `${day} ${monthName} `;
    }
  }

  // Handle day + numeric month + year: "01 01 2025" → "01 Jan 2025 - "
  const dayNumericMonthYearMatch = result.match(/^(\d{1,2})\s+(\d{2})\s+(\d{4})$/);
  if (dayNumericMonthYearMatch) {
    const [, day, numMonth, year] = dayNumericMonthYearMatch;
    const dayNum = parseInt(day, 10);
    const monthName = getMonthFromNumeric(numMonth);
    if (dayNum >= 1 && dayNum <= 31 && monthName) {
      return `${day} ${monthName} ${year} - `;
    }
  }

  return null;
};

const formatSecondDate = (result: string): string | null => {
  // After separator: date with text month and separators: 01/Jan/2025, 01-jan-2025 → 01 Jan 2025
  const afterSepSeparatedTextMonthMatch = result.match(/^(.+\s-\s)(\d{1,2})[/\-_.]([A-Za-z]{3})[/\-_.](\d{4})$/);
  if (afterSepSeparatedTextMonthMatch && isValidMonthAbbrev(afterSepSeparatedTextMonthMatch[3])) {
    const [, prefix, day, monthStr, year] = afterSepSeparatedTextMonthMatch;
    const dayNum = parseInt(day, 10);
    const month = normalizeMonthName(monthStr);
    if (dayNum >= 1 && dayNum <= 31) {
      return `${prefix}${day.padStart(2, '0')} ${month} ${year}`;
    }
  }

  // After separator: date with space before separator and text month: "01 /Jan/2025" → 01 Jan 2025
  const afterSepSpaceBeforeSeparatorTextMonthMatch = result.match(
    /^(.+\s-\s)(\d{1,2})\s*[/\-_.]([A-Za-z]{3})[/\-_.](\d{4})$/,
  );
  if (afterSepSpaceBeforeSeparatorTextMonthMatch && isValidMonthAbbrev(afterSepSpaceBeforeSeparatorTextMonthMatch[3])) {
    const [, prefix, day, monthStr, year] = afterSepSpaceBeforeSeparatorTextMonthMatch;
    const dayNum = parseInt(day, 10);
    const month = normalizeMonthName(monthStr);
    if (dayNum >= 1 && dayNum <= 31) {
      return `${prefix}${day.padStart(2, '0')} ${month} ${year}`;
    }
  }

  // After separator: date with space before separator and numeric month: "01 /01/2025" → 01 Jan 2025
  const afterSepSpaceBeforeSeparatorNumericMonthMatch = result.match(
    /^(.+\s-\s)(\d{1,2})\s*[/\-_.](\d{2})[/\-_.](\d{4})$/,
  );
  if (afterSepSpaceBeforeSeparatorNumericMonthMatch) {
    const [, prefix, day, numMonth, year] = afterSepSpaceBeforeSeparatorNumericMonthMatch;
    const dayNum = parseInt(day, 10);
    const monthName = getMonthFromNumeric(numMonth);
    if (dayNum >= 1 && dayNum <= 31 && monthName) {
      return `${prefix}${day.padStart(2, '0')} ${monthName} ${year}`;
    }
  }

  // After separator: date with numeric month and separators: 01/01/2025 → 01 Jan 2025
  const afterSepSeparatedNumericMonthMatch = result.match(/^(.+\s-\s)(\d{1,2})[/\-_.](\d{2})[/\-_.](\d{4})$/);
  if (afterSepSeparatedNumericMonthMatch) {
    const [, prefix, day, numMonth, year] = afterSepSeparatedNumericMonthMatch;
    const dayNum = parseInt(day, 10);
    const monthName = getMonthFromNumeric(numMonth);
    if (dayNum >= 1 && dayNum <= 31 && monthName) {
      return `${prefix}${day.padStart(2, '0')} ${monthName} ${year}`;
    }
  }

  // After separator: compact format 01012025 → 01 Jan 2025
  const afterSepCompactFullMatch = result.match(/^(.+\s-\s)(\d{2})(\d{2})(\d{4})$/);
  if (afterSepCompactFullMatch) {
    const [, prefix, day, numMonth, year] = afterSepCompactFullMatch;
    const dayNum = parseInt(day, 10);
    const monthName = getMonthFromNumeric(numMonth);
    if (dayNum >= 1 && dayNum <= 31 && monthName) {
      return `${prefix}${day} ${monthName} ${year}`;
    }
  }

  // After separator: compact day+month 0101 → 01 Jan
  const afterSepCompactDayMonthMatch = result.match(/^(.+\s-\s)(\d{2})(\d{2})$/);
  if (afterSepCompactDayMonthMatch) {
    const [, prefix, day, numMonth] = afterSepCompactDayMonthMatch;
    const dayNum = parseInt(day, 10);
    const monthName = getMonthFromNumeric(numMonth);
    if (dayNum >= 1 && dayNum <= 31 && monthName) {
      return `${prefix}${day} ${monthName} `;
    }
  }

  // After separator: day + numeric month "01 01" → "01 Jan "
  const afterSepDayNumericMonthMatch = result.match(/^(.+\s-\s)(\d{1,2})\s+(\d{2})$/);
  if (afterSepDayNumericMonthMatch) {
    const [, prefix, day, numMonth] = afterSepDayNumericMonthMatch;
    const dayNum = parseInt(day, 10);
    const monthName = getMonthFromNumeric(numMonth);
    if (dayNum >= 1 && dayNum <= 31 && monthName) {
      return `${prefix}${day} ${monthName} `;
    }
  }

  // After separator: full date with text month
  const afterSepFullDateMatch = result.match(/^(.+\s-\s)(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/i);
  if (afterSepFullDateMatch && isValidMonthAbbrev(afterSepFullDateMatch[3])) {
    const [, prefix, day, monthStr, year] = afterSepFullDateMatch;
    const month = normalizeMonthName(monthStr);
    return `${prefix}${day} ${month} ${year}`;
  }

  // After separator: day + numeric month + year "01 01 2025" → "01 Jan 2025"
  const afterSepDayNumMonthYearMatch = result.match(/^(.+\s-\s)(\d{1,2})\s+(\d{2})\s+(\d{4})$/);
  if (afterSepDayNumMonthYearMatch) {
    const [, prefix, day, numMonth, year] = afterSepDayNumMonthYearMatch;
    const dayNum = parseInt(day, 10);
    const monthName = getMonthFromNumeric(numMonth);
    if (dayNum >= 1 && dayNum <= 31 && monthName) {
      return `${prefix}${day} ${monthName} ${year}`;
    }
  }

  return null;
};

export const autoFormatDateRangeInput = (newValue: string, prevValue: string): string => {
  if (newValue.length < prevValue.length) {
    return newValue;
  }

  let result = newValue.trim();

  // Try formatting first date patterns
  const firstDateFormatted = formatFirstDate(result);
  if (firstDateFormatted) return firstDateFormatted;

  // Handle two-digit day: "01" → "01 "
  const dayMatch = result.match(/^(\d{2})$/);
  if (dayMatch) {
    const day = parseInt(dayMatch[1], 10);
    if (day >= 1 && day <= 31) {
      result = `${dayMatch[1]} `;
    }
  }

  // Handle day + month abbreviation (case insensitive): "01 jan" → "01 Jan "
  const dayMonthMatch = result.match(/^(\d{1,2})\s+([A-Za-z]{3})$/i);
  if (dayMonthMatch && isValidMonthAbbrev(dayMonthMatch[2])) {
    const month = normalizeMonthName(dayMonthMatch[2]);
    result = `${dayMonthMatch[1]} ${month} `;
  }

  // Handle full start date: "01 Jan 2025" or "01 jan 2025" → "01 Jan 2025 - "
  const fullStartDateMatch = result.match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/i);
  if (fullStartDateMatch && isValidMonthAbbrev(fullStartDateMatch[2])) {
    const month = normalizeMonthName(fullStartDateMatch[2]);
    result = `${fullStartDateMatch[1]} ${month} ${fullStartDateMatch[3]} - `;
  }

  // Try formatting second date patterns
  const secondDateFormatted = formatSecondDate(result);
  if (secondDateFormatted) return secondDateFormatted;

  // After separator: two-digit day "01" → "01 "
  const afterSeparatorDayMatch = result.match(/^(.+\s-\s)(\d{2})$/);
  if (afterSeparatorDayMatch) {
    const day = parseInt(afterSeparatorDayMatch[2], 10);
    if (day >= 1 && day <= 31) {
      result = `${afterSeparatorDayMatch[1]}${afterSeparatorDayMatch[2]} `;
    }
  }

  // After separator: month abbreviation (case insensitive)
  const afterSeparatorMonthMatch = result.match(/^(.+\s-\s\d{1,2})\s+([A-Za-z]{3})$/i);
  if (afterSeparatorMonthMatch && isValidMonthAbbrev(afterSeparatorMonthMatch[2])) {
    const prefix = afterSeparatorMonthMatch[1];
    const month = normalizeMonthName(afterSeparatorMonthMatch[2]);
    result = `${prefix} ${month} `;
  }

  return result;
};
